import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { MapsKeyProvider } from '@/lib/MapsKeyContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import AppLayout from '@/components/layout/AppLayout';
import Analytics from '@/lib/Analytics';
import RouteMeta from '@/lib/RouteMeta';

// Pages
import Home from '@/pages/Home';
import Search from '@/pages/Search';
import AddCoffee from '@/pages/AddCoffee';
import MyPage from '@/pages/MyPage';
import Admin from '@/pages/Admin';
import CoffeeDetail from '@/pages/CoffeeDetail';
import UserProfile from '@/pages/UserProfile';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Contact from '@/pages/Contact';

const AuthenticatedApp = () => {
  const { isLoadingAuth } = useAuth();

  // Only the session restore is blocking. Everything downstream of it —
  // the Maps key, public data — loads without holding up the first paint.
  if (isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="space-y-3 text-center">
          <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto" />
          <p className="text-xs text-muted-foreground font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<Search />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/add" element={<AddCoffee />} />
          <Route path="/my" element={<MyPage />} />
          <Route path="/admin" element={<Admin />} />
        </Route>
        <Route path="/coffee/:id" element={<CoffeeDetail />} />
        <Route path="/user/:id" element={<UserProfile />} />
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <MapsKeyProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router>
            {/* Before Analytics: it reads document.title for the page_view. */}
            <RouteMeta />
            <Analytics />
            <AuthenticatedApp />
          </Router>
          <Toaster />
        </QueryClientProvider>
      </MapsKeyProvider>
    </AuthProvider>
  );
}

export default App;